<template>
  <section
    id="about"
    class="py-16 sm:py-20 bg-annapolis-navy"
    aria-labelledby="about-heading"
  >
    <div class="container mx-auto px-4 sm:px-6 max-w-4xl">
      <div class="text-center mb-8 sm:mb-12">
        <h2
          id="about-heading"
          class="text-fluid-3xl sm:text-fluid-4xl md:text-fluid-5xl font-bold text-white mb-4 sm:mb-6"
        >
          About Annapolis Labs
        </h2>
      </div>

      <div class="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-300 leading-relaxed">
        <p>
          At Annapolis Labs, we build professional, high-quality software solutions.
          From specialized tools for developers to robust custom web applications,
          we create reliable and secure systems that solve real business challenges without unnecessary complexity.
        </p>

        <p>
          Whether you're an indie game developer protecting your distribution channels,
          a software vendor managing licensing at scale, or an agency looking for a powerful custom web platform,
          we deliver tailored solutions that help you succeed.
        </p>

        <p>
          We’ve expanded our expertise to include modern web development — building sophisticated,
          scalable web applications and enterprise platforms.
        </p>

        <p>
          Security, reliability, and craftsmanship remain at the core of everything we do. Every solution is built with precision,
          thoroughly tested, and designed to perform exceptionally across platforms and at scale.
        </p>
      </div>

      <!-- Trust Indicators -->
      <div
        class="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8"
        role="list"
        aria-label="Company values"
      >
        <div
          class="text-center"
          role="listitem"
        >
          <div class="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-annapolis-teal-dark to-annapolis-teal rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 sm:h-8 sm:w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 class="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Security First</h3>
          <p class="text-gray-400 text-sm sm:text-base">Military-grade encryption and secure by design</p>
        </div>

        <div
          class="text-center"
          role="listitem"
        >
          <div class="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-annapolis-teal-dark to-annapolis-teal rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 sm:h-8 sm:w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </div>
          <h3 class="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Developer-Focused</h3>
          <p class="text-gray-400 text-sm sm:text-base">Built by developers who understand your needs</p>
        </div>

        <div
          class="text-center"
          role="listitem"
        >
          <div class="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-annapolis-teal-dark to-annapolis-teal rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 sm:h-8 sm:w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 class="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">Battle-Tested</h3>
          <p class="text-gray-400 text-sm sm:text-base">Reliable tools you can depend on</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/**
 * AboutSection component displays information about Annapolis Labs
 * @component
 * @example
 * <AboutSection />
 */
defineOptions({
  name: 'AboutSection'
})
</script>
